'use client';

import { useCallback, useEffect } from 'react';

interface AnchorLinkManagerProps {
    offset?: number;
    duration?: number;
    onNavigate?: (anchor: string) => void;
}

const AnchorLinkManager = ({ offset = 80, duration = 800, onNavigate }: AnchorLinkManagerProps) => {
    const getOffsetForViewport = useCallback(() => (window.innerWidth < 768 ? 50 : offset), [offset]);

    const getTargetElement = useCallback((selector: string) => {
        if (!selector || selector === '#' || selector.trim().length <= 1) return null;

        try {
            return document.querySelector(selector);
        } catch (_error) {
            console.warn(`Invalid selector: ${selector}`);
            return null;
        }
    }, []);

    const updateHash = useCallback((anchor: string) => {
        if (window.location.hash !== anchor) {
            window.history.replaceState(null, '', anchor);
        }
    }, []);

    const scrollToAnchor = useCallback((anchor: string, behavior: ScrollBehavior = 'smooth') => {
        const targetElement = getTargetElement(anchor);
        if (!targetElement) return false;

        const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - getOffsetForViewport();

        window.scrollTo({
            top: offsetPosition,
            behavior,
        });
        updateHash(anchor);
        onNavigate?.(anchor);
        return true;
    }, [getOffsetForViewport, getTargetElement, onNavigate, updateHash]);

    useEffect(() => {
        const handleAnchorClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const anchor = target.closest('a[href^="#"]') as HTMLAnchorElement;

            if (anchor) {
                e.preventDefault();
                const href = anchor.getAttribute('href');

                if (!href || href === '#' || href.trim().length <= 1) return;

                const targetElement = getTargetElement(href);
                if (!targetElement) return;

                const startPosition = window.scrollY;
                const offsetPosition = targetElement.getBoundingClientRect().top + window.scrollY - getOffsetForViewport();
                const distance = offsetPosition - startPosition;
                const startTime = performance.now();

                const easeInOutCubic = (t: number): number => {
                    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
                };

                const animateScroll = (currentTime: number) => {
                    const elapsed = currentTime - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    const ease = easeInOutCubic(progress);

                    window.scrollTo(0, startPosition + distance * ease);

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    } else {
                        updateHash(href);
                        onNavigate?.(href);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        };

        const handleButtonClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const button = target.closest('button[data-anchor]') as HTMLButtonElement;

            if (button) {
                const anchor = button.getAttribute('data-anchor');

                if (!anchor || anchor === '#' || anchor.trim().length <= 1) return;
                scrollToAnchor(anchor);
            }
        };

        document.addEventListener('click', handleAnchorClick);
        document.addEventListener('click', handleButtonClick);

        return () => {
            document.removeEventListener('click', handleAnchorClick);
            document.removeEventListener('click', handleButtonClick);
        };
    }, [duration, getOffsetForViewport, getTargetElement, onNavigate, scrollToAnchor, updateHash]);

    useEffect(() => {
        const scrollToHashWithRetry = (hash: string, attemptsLeft = 12) => {
            if (!hash || hash === '#' || hash.trim().length <= 1) return;
            if (scrollToAnchor(hash, 'auto') || attemptsLeft <= 1) return;

            window.setTimeout(() => {
                scrollToHashWithRetry(hash, attemptsLeft - 1);
            }, 120);
        };

        const syncHashPosition = () => {
            scrollToHashWithRetry(window.location.hash);
        };

        syncHashPosition();
        window.addEventListener('hashchange', syncHashPosition);

        return () => window.removeEventListener('hashchange', syncHashPosition);
    }, [scrollToAnchor]);

    return null;
};

export default AnchorLinkManager;
